"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  maximumPossible,
  minimumPossible,
  type Roll,
  rollDice,
} from "@/lib/tool-kit/random-tools/dice";

const SIDE_PRESETS = [4, 6, 8, 10, 12, 20, 100];

const MAX_DICE = 20;
const MIN_SIDES = 2;
const MAX_SIDES = 1000;

function clampInt(value: string, min: number, max: number, fallback: number) {
  const parsed = parseIntegerInput(value);
  if (parsed === null) {
    return fallback;
  }
  return Math.min(max, Math.max(min, parsed));
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const DICE_ROLLER_STRINGS = {
  numberOfDice: "Number of dice",
  diceRange: "Dice (1–{max})",
  sidesPerDie: "Sides per die",
  custom: "Custom",
  customPlaceholder: "e.g. 30",
  rolling: "Rolling {dice}",
  rollingCustomDie: "Rolling {dice} (custom die)",
  rollDice: "Roll {dice}",
  clear: "Clear",
  rollResult: "{notation} result",
  totalLine: "Total: {total}",
  total: "Total",
  averagePerDie: "Average per die",
  minimumPossible: "Minimum possible",
  maximumPossible: "Maximum possible",
  rollHistoryLast10: "Roll history (last 10)",
} as const;

export default function DiceRollerWidget() {
  const [diceCount, setDiceCount] = useState(2);
  const [sides, setSides] = useState(6);
  const [customSides, setCustomSides] = useState("");
  const [lastRoll, setLastRoll] = useState<Roll | null>(null);
  const [history, setHistory] = useState<Roll[]>([]);
  const [flash, setFlash] = useState(false);

  const strings = useToolUi(DICE_ROLLER_STRINGS);
  const fmt = useToolNumber();

  const usingPreset = SIDE_PRESETS.includes(sides);

  function handleRoll() {
    const roll = rollDice(diceCount, sides);
    setLastRoll(roll);
    setHistory((prev) => [roll, ...prev].slice(0, 10));
    setFlash(true);
    setTimeout(() => setFlash(false), 500);
  }

  function handleCustomSides(value: string) {
    setCustomSides(value);
    const parsed = parseIntegerInput(value);
    if (parsed !== null && parsed >= MIN_SIDES && parsed <= MAX_SIDES) {
      setSides(parsed);
    }
  }

  function handleClear() {
    setLastRoll(null);
    setHistory([]);
  }

  const average =
    lastRoll === null
      ? "—"
      : fmt(lastRoll.total / lastRoll.dice.length, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        });
  const highlightClass = flash
    ? "border-primary bg-primary/10"
    : "border-border bg-card";

  return (
    <>
      {/* Settings */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.numberOfDice}
        </p>
        <div className="flex flex-wrap items-center gap-4">
          <label
            className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm"
            htmlFor="dice-count"
          >
            {t(strings.diceRange, { max: MAX_DICE })}
            <input
              className="w-16 rounded border border-border bg-background px-2 py-1 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="dice-count"
              max={MAX_DICE}
              min={1}
              onChange={(e) =>
                setDiceCount(clampInt(e.target.value, 1, MAX_DICE, 1))
              }
              type="number"
              value={diceCount}
            />
          </label>
        </div>
        <p className="font-medium text-foreground text-sm">
          {strings.sidesPerDie}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          {SIDE_PRESETS.map((preset) => (
            <button
              className={`rounded-lg border px-4 py-2 font-medium text-sm transition-colors ${
                sides === preset
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-border bg-card text-muted-foreground hover:bg-muted"
              }`}
              key={preset}
              onClick={() => {
                setSides(preset);
                setCustomSides("");
              }}
              type="button"
            >
              d{preset}
            </button>
          ))}
          <label
            className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm"
            htmlFor="custom-sides"
          >
            {strings.custom}
            <input
              className="w-20 rounded border border-border bg-background px-2 py-1 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="custom-sides"
              max={MAX_SIDES}
              min={MIN_SIDES}
              onChange={(e) => handleCustomSides(e.target.value)}
              placeholder={strings.customPlaceholder}
              type="number"
              value={customSides}
            />
          </label>
        </div>
        <p className="text-muted-foreground text-xs">
          {t(usingPreset ? strings.rolling : strings.rollingCustomDie, {
            dice: `${diceCount}d${sides}`,
          })}
        </p>
      </section>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
          onClick={handleRoll}
          type="button"
        >
          {t(strings.rollDice, { dice: `${diceCount}d${sides}` })}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={lastRoll === null && history.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Result */}
      {lastRoll !== null && (
        <div
          className={`space-y-4 rounded-lg border p-6 text-center transition-colors duration-500 ${highlightClass}`}
        >
          <p className="text-muted-foreground text-sm">
            {t(strings.rollResult, { notation: lastRoll.notation })}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {lastRoll.dice.map((die, index) => (
              <span
                className="flex h-14 w-14 items-center justify-center rounded-lg border border-border bg-background font-semibold text-foreground text-xl"
                key={`die-${String(index)}-${die}`}
              >
                {die}
              </span>
            ))}
          </div>
          <p className="font-semibold text-3xl text-foreground">
            {t(strings.totalLine, { total: lastRoll.total })}
          </p>
        </div>
      )}

      {/* Stats */}
      {lastRoll !== null && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label={strings.total} value={lastRoll.total} />
          <StatCard label={strings.averagePerDie} value={average} />
          <StatCard
            label={strings.minimumPossible}
            value={minimumPossible(lastRoll)}
          />
          <StatCard
            label={strings.maximumPossible}
            value={maximumPossible(lastRoll)}
          />
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.rollHistoryLast10}
          </h2>
          <ol className="space-y-1 rounded-lg border border-border bg-card p-5 font-mono text-foreground text-sm leading-relaxed">
            {history.map((roll, index) => (
              <li key={`${roll.notation}-${roll.total}-${String(index)}`}>
                {roll.notation}: {roll.dice.join("+")} = {roll.total}
              </li>
            ))}
          </ol>
        </section>
      )}
    </>
  );
}
